import { tr } from "../i18n/LanguageContext";
import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { X, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import { getCroppedImageBlob } from "../utils/cropImage";

/**
 * Shared crop-before-upload modal used by both event image upload
 * (AddEventPage, aspect 16:9) and profile avatar upload (ProfilePage,
 * aspect 1:1 with a circular preview overlay).
 *
 * Props:
 *   imageSrc:      data URL of the raw file the user just selected
 *   aspect:        number (width/height) — 16/9 for events, 1 for avatars
 *   cropShape:     "rect" | "round" — controls the react-easy-crop overlay shape
 *   title:         modal heading (Russian, defaults are provided)
 *   onCancel:      called when the user backs out (no upload)
 *   onConfirm:     called with a Blob of the cropped image; the caller is
 *                  responsible for actually uploading it.
 *   confirming:    when true, the confirm button shows a spinner and is disabled
 *                  (parent uses this while the upload is in flight)
 */
export default function ImageCropModal({
  imageSrc,
  aspect,
  cropShape = "rect",
  title,
  onCancel,
  onConfirm,
  confirming = false,
  initialCrop,
  initialZoom,
  onStateChange,
}) {
  const [crop, setCrop] = useState(initialCrop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialZoom || 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [preparing, setPreparing] = useState(false);

  // Bubble crop/zoom changes up so the parent can persist them for the
  // current form session and restore on re-open of the same image.
  useEffect(() => {
    onStateChange?.({ crop, zoom });
  }, [crop, zoom, onStateChange]);

  // Lock body scroll while the modal is open — same behaviour used by the
  // date-picker modal, keeps behind-content from scrolling on iOS.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes the modal (cancel).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !confirming && !preparing) onCancel?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, confirming, preparing]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setPreparing(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      await onConfirm(blob);
    } finally {
      setPreparing(false);
    }
  };

  const busy = confirming || preparing;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="image-crop-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
    >
      <div
        className="w-full max-w-[560px] rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        style={{ backgroundColor: "var(--eunoia-pure)", maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--eunoia-whisper)" }}>
          <h2 className="eunoia-title-small" style={{ color: "var(--eunoia-deep)" }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-[color:var(--eunoia-glow)] transition-colors disabled:opacity-50"
            aria-label={tr("Закрыть")}
            data-testid="image-crop-close"
          >
            <X className="h-4 w-4" style={{ color: "var(--eunoia-soft)" }} />
          </button>
        </div>

        {/* Crop surface */}
        <div className="relative w-full" style={{ height: "min(58vh, 420px)", backgroundColor: "var(--eunoia-glow)" }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={cropShape === "rect"}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </div>

        {/* Zoom slider */}
        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 flex-shrink-0" style={{ color: "var(--eunoia-soft)" }} />
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[color:var(--eunoia-accent)]"
              style={{ accentColor: "var(--eunoia-accent)" }}
              aria-label={tr("Масштаб")}
              data-testid="image-crop-zoom"
            />
            <ZoomIn className="h-4 w-4 flex-shrink-0" style={{ color: "var(--eunoia-soft)" }} />
          </div>
          <p className="text-xs" style={{ color: "var(--eunoia-soft)" }}>
            {tr("Перетащите фото, чтобы выбрать нужную область. Используйте ползунок для масштаба.")}
          </p>
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-2 border-t" style={{ borderColor: "var(--eunoia-whisper)" }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="eunoia-btn-secondary disabled:opacity-50"
            data-testid="image-crop-cancel"
          >
            {tr("Отмена")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
            className="eunoia-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="image-crop-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {tr("Готово")}
          </button>
        </div>
      </div>
    </div>
  );
}
